import {
  Box,
  Button,
  HStack,
  Input,
  InputProps,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { InfoTooltip } from '$/components/common/InfoTooltip';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { FeedbackSection } from '$/components/core/FeedbackModal/components/FeedbackSection';
import { useActiveUserCountry } from '$/hooks/useActiveUserCountry';
import { useFeedbackModal } from '$/hooks/useFeedbackModal';
import { useToast } from '$/hooks/useToast';
import { sendFeedback } from '$/services/usecases/feedback';
import { emailFormatRegex } from '$/utils/regexUtils';

export const FeedbackModal = () => {
  const { t } = useTranslation();
  const [issue, setIssue] = useState('');
  const [idea, setIdea] = useState('');
  const user = useAuthenticationStore.useUser();
  const [email, setEmail] = useState('');
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isOpen = useFeedbackModal.useIsOpen();
  const onClose = useFeedbackModal.useOnClose();
  const toast = useToast();

  const country = useActiveUserCountry();
  const isEmailValid = emailFormatRegex.test(email);

  const submittingDisabled = (issue === '' && idea === '') || !isEmailValid;

  const onCloseModal = () => {
    setIssue('');
    setIdea('');
    setEmail('');
    setIsLoading(false);
    setIsEmailTouched(false);
    onClose();
  };

  useEffect(() => {
    if (user != null) {
      setEmail(user.email);
    } else {
      setEmail('');
    }
  }, [user, isOpen]);

  const onSubmit = async () => {
    setIsLoading(true);
    const response = await sendFeedback({
      email,
      idea,
      issue,
    });

    if (response.isSuccessful) {
      toast(
        t('feedback.feedbackSentHeader'),
        'success',
        t('feedback.feedbackSentDescription'),
      );

      onCloseModal();
    }
  };

  const invalidInputProps: InputProps = {
    borderColor: 'form.errorBorder !important',
    border: '2px solid',
    _focus: {
      borderColor: 'form.errorBorder',
      border: '2px solid',
    },
    _active: {
      borderColor: 'form.errorBorder',
      border: '2px solid',
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent
        maxW='594px'
        mx='0'
        my='6'
        p={{ base: '4', mobile: '10' }}
        color='text'
      >
        <HStack justify='space-between' pb='4'>
          <Text
            p='0'
            fontSize={{ base: 'h10', mobile: 'h7' }}
            fontWeight='bold'
          >
            {t('feedback.modalTitle')}
          </Text>

          <ModalCloseButton pos='static' />
        </HStack>

        <ModalBody p='0'>
          <VStack gap='6'>
            <Text fontSize='sm'>{t('feedback.modalDescription')}</Text>

            <FeedbackSection
              description={t('feedback.issueDescription')}
              icon='bug'
              title={t('feedback.issueTitle')}
              inputPlaceholder={t('feedback.issuePlaceholder')}
              inputValue={issue}
              onChange={setIssue}
            />
            <FeedbackSection
              description={t('feedback.ideaDescription')}
              icon='lightbulb'
              iconSize='6'
              title={t('feedback.ideaTitle')}
              inputPlaceholder={t('feedback.ideaPlaceholder')}
              inputValue={idea}
              onChange={setIdea}
            />
            <FeedbackSection
              additionalBlock={
                <>
                  <Box as='span' mr='1'>
                    {t('feedback.helpDescriptionWithoutEmail')}
                  </Box>
                  <Link
                    color='primaryButton.background'
                    fontWeight='bold'
                    href={`mailto:${country?.contactEmail}`}
                  >
                    {country?.contactEmail}
                  </Link>
                </>
              }
              icon='question'
              title={t('feedback.helpTitle')}
            />
            {user == null ? (
              <Box color='text'>
                <Text mb='2' fontSize='sm'>
                  {t('feedback.emailDescription')}
                </Text>
                <HStack justify='space-between' mb='2'>
                  <Text fontSize='sm'>
                    {t('dashboard.authentication.emailLabel')}
                  </Text>
                  <InfoTooltip
                    info={t(
                      'dashboard.authentication.errorMessages.emailRules',
                    )}
                    icon={
                      <Icon
                        w='12px'
                        color='lighterText'
                        cursor='help'
                        icon='info'
                      />
                    }
                  />
                  <Spacer />
                  {!isEmailValid && isEmailTouched && (
                    <Text color='form.errorText' fontSize='sm'>
                      {t('dashboard.authentication.errorMessages.wrongFormat')}
                    </Text>
                  )}
                </HStack>
                <Input
                  onBlur={() => setIsEmailTouched(true)}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('feedback.emailPlaceholder')}
                  type='email'
                  value={email}
                  {...(!isEmailValid && isEmailTouched
                    ? invalidInputProps
                    : {})}
                />
              </Box>
            ) : (
              <Text fontSize='sm'>
                {
                  <Trans
                    i18nKey='feedback.loggedInInfo'
                    values={{
                      email: user.email,
                    }}
                  />
                }
              </Text>
            )}
            <HStack justify='space-between' w='full' mt='4'>
              <Button
                fontSize='sm'
                isLoading={isLoading}
                onClick={onCloseModal}
                variant='text'
              >
                {t('general.cancel')}
              </Button>
              <Button
                fontSize='sm'
                isDisabled={submittingDisabled}
                isLoading={isLoading}
                onClick={onSubmit}
              >
                {t('feedback.sendButton')}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
